import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { formatDuration, formatDurationLabel } from 'utils/videoUtils';
import { publishToGallery } from '../../services/computeWallet';

// Animated canvas background that reacts to audio
function AudioReactiveBackground({ audioRef, playing }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const analyserRef = useRef(null);
  const audioCtxRef = useRef(null);
  const connectedRef = useRef(false);
  const timeRef = useRef(0);
  const particlesRef = useRef([]);

  const initParticles = useCallback((w, h) => {
    particlesRef.current = Array.from({ length: 120 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 1.5,
      vy: (Math.random() - 0.5) * 1.5,
      life: Math.random(),
      size: Math.random() * 3 + 1,
      hue: Math.random() * 60 + 300,
    }));
  }, []);

  useEffect(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return;
    const W = canvas?.offsetWidth || 640;
    const H = canvas?.offsetHeight || 360;
    canvas.width = W;
    canvas.height = H;
    initParticles(W, H);

    const ctx = canvas?.getContext('2d');
    let freqData = new Uint8Array(128);

    const setupAudio = () => {
      if (connectedRef?.current || !audioRef?.current) return;
      try {
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = audioCtx;
        const analyser = audioCtx?.createAnalyser();
        analyser.fftSize = 256;
        analyserRef.current = analyser;
        freqData = new Uint8Array(analyser.frequencyBinCount);
        const source = audioCtx?.createMediaElementSource(audioRef?.current);
        source?.connect(analyser);
        analyser?.connect(audioCtx?.destination);
        connectedRef.current = true;
      } catch {}
    };

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      timeRef.current++;
      const t = timeRef?.current;

      if (analyserRef?.current) analyserRef?.current?.getByteFrequencyData(freqData);
      const avg = freqData?.reduce((a, b) => a + b, 0) / freqData?.length;
      const amplitude = avg / 255;

      // Background
      ctx.fillStyle = `rgba(5,0,20,${playing ? 0.12 : 0.06})`;
      ctx?.fillRect(0, 0, W, H);

      // Animated gradient rings
      const cx = W / 2, cy = H / 2;
      for (let r = 0; r < 4; r++) {
        const radius = (80 + r * 60) * (1 + amplitude * 0.8);
        const hue = (r * 40 + t * 0.3) % 360;
        const grad = ctx?.createRadialGradient(cx, cy, radius * 0.3, cx, cy, radius);
        grad?.addColorStop(0, `hsla(${hue}, 80%, 60%, ${0.08 + amplitude * 0.12})`);
        grad?.addColorStop(1, 'transparent');
        ctx?.beginPath();
        ctx?.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx?.fill();
      }

      // Particles
      particlesRef?.current?.forEach((p) => {
        p.x += p?.vx * (1 + amplitude * 4);
        p.y += p?.vy * (1 + amplitude * 4);
        p.life -= 0.005;
        if (p?.x < 0 || p?.x > W || p?.y < 0 || p?.y > H || p?.life <= 0) {
          p.x = cx + (Math.random() - 0.5) * W * 0.6;
          p.y = cy + (Math.random() - 0.5) * H * 0.6;
          p.vx = (Math.random() - 0.5) * 1.5;
          p.vy = (Math.random() - 0.5) * 1.5;
          p.life = 1;
          p.size = Math.random() * 3 + 1;
          p.hue = Math.random() * 60 + 300;
        }
        const size = p?.size * (1 + amplitude * 2.5);
        ctx?.beginPath();
        ctx?.arc(p?.x, p?.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p?.hue + t * 0.3}, 85%, 65%, ${p?.life * 0.7})`;
        ctx?.fill();
      });

      // Waveform bars at bottom
      if (playing) {
        const barW = W / freqData?.length;
        for (let i = 0; i < freqData?.length; i++) {
          const barH = (freqData?.[i] / 255) * (H * 0.25);
          const hue = 340 + i * 0.5;
          ctx.fillStyle = `hsla(${hue}, 80%, 60%, 0.6)`;
          ctx?.fillRect(i * barW, H - barH, barW - 0.5, barH);
        }
      }
    };

    draw();

    if (playing) setupAudio();

    return () => {
      cancelAnimationFrame(animRef?.current);
    };
  }, [playing, audioRef, initParticles]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.9 }}
    />
  );
}

function WaveformBar({ waveform, currentTime, duration, onSeek }) {
  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bars = waveform?.length > 0 ? waveform : Array.from({ length: 80 }, (_, i) => 0.3 + Math.abs(Math.sin(i * 0.3)) * 0.7);
  return (
    <div
      className="relative h-12 flex items-end gap-px overflow-hidden rounded-lg cursor-pointer"
      onClick={(e) => {
        if (!onSeek) return;
        const rect = e?.currentTarget?.getBoundingClientRect();
        const p = (e?.clientX - rect?.left) / rect?.width;
        onSeek(p * duration);
      }}
    >
      {bars?.map((v, i) => {
        const barPct = (i / bars?.length) * 100;
        const isPast = barPct <= pct;
        return (
          <div
            key={i}
            className="flex-1 rounded-sm transition-all duration-75"
            style={{
              height: `${Math.max(10, v * 100)}%`,
              background: isPast ? 'linear-gradient(to top, #F43F5E, #8B5CF6)' : 'rgba(255,255,255,0.15)',
            }}
          />
        );
      })}
    </div>
  );
}

export default function MusicVideoPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  // Always read fresh item from location.state — never cache between navigations
  const item = location?.state?.item || location?.state?.game || null;

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [waveform] = useState(() => Array.from({ length: 80 }, (_, i) => 0.2 + Math.abs(Math.sin(i * 0.4)) * 0.8));
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState(null);
  const [toast, setToast] = useState(null);

  const videoUrl = item?.videoUrl || null;
  const audioUrl = item?.audioUrl || null;
  const hasRealVideo = !!(videoUrl && videoUrl !== '#');
  const hasAudio = !!(audioUrl && audioUrl !== '#');
  const isTestMode = item?.testMode || false;
  // Stable video ID for download filename
  const videoId = item?.videoId || item?.id || 'music-video';

  const handlePlayPause = useCallback(() => {
    if (hasRealVideo && videoRef?.current) {
      if (playing) { videoRef?.current?.pause(); setPlaying(false); }
      else { videoRef?.current?.play()?.then(() => setPlaying(true))?.catch(() => setPlaying(false)); }
    } else if (hasAudio && audioRef?.current) {
      if (playing) { audioRef?.current?.pause(); setPlaying(false); }
      else { audioRef?.current?.play()?.then(() => setPlaying(true))?.catch(() => setPlaying(false)); }
    } else {
      setPlaying((v) => !v);
    }
  }, [playing, hasRealVideo, hasAudio]);

  const handleSeek = useCallback((time) => {
    const t = Math.max(0, Math.min(time, duration));
    setCurrentTime(t);
    if (hasRealVideo && videoRef?.current) videoRef.current.currentTime = t;
    if (hasAudio && audioRef?.current) audioRef.current.currentTime = t;
  }, [duration, hasRealVideo, hasAudio]);

  const handleSeekBar = (e) => {
    const rect = e?.currentTarget?.getBoundingClientRect();
    const pct = (e?.clientX - rect?.left) / rect?.width;
    handleSeek(pct * (duration || 0));
  };

  const handleVolumeChange = (v) => {
    setVolume(v);
    setMuted(v === 0);
    if (videoRef?.current) videoRef.current.volume = v;
    if (audioRef?.current) audioRef.current.volume = v;
  };

  const handleMute = () => {
    const newMuted = !muted;
    setMuted(newMuted);
    if (videoRef?.current) videoRef.current.muted = newMuted;
    if (audioRef?.current) audioRef.current.muted = newMuted;
  };

  const handleFullscreen = () => {
    const el = containerRef?.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleDownload = () => {
    // Use videoUrl (blob URL) for download — it is the actual rendered video
    const url = videoUrl || audioUrl;
    if (!url || url === '#') return;
    const link = document.createElement('a');
    link.href = url;
    const ext = item?.mimeType?.includes('mp4') ? 'mp4' : 'webm';
    link.download = `${item?.title || 'music-video'}-${videoId}.${ext}`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const handlePublishToGallery = useCallback(async () => {
    if (!item || publishSuccess) return;
    setIsPublishing(true);
    setPublishError(null);
    try {
      const { data: { user } } = await import('../../lib/supabase')?.then(m => m?.supabase?.auth?.getUser());
      if (!user?.id) throw new Error('You must be signed in to publish.');
      await publishToGallery({
        userId: user?.id,
        title: item?.title || 'My Music Video',
        generationType: 'video',
        prompt: item?.prompt || '',
        thumbnailUrl: item?.thumbnail || null,
        videoUrl: item?.videoUrl || null,
        audioUrl: item?.audioUrl || null,
        allowRemix: true,
        metadata: {
          visualStyle: item?.visualStyle,
          bpm: item?.bpm,
          duration: item?.duration,
          captionMode: item?.captionMode,
          pipeline: 'music-to-video',
        },
      });
      setPublishSuccess(true);
      showToast('Published to Community Gallery!', 'success');
    } catch (err) {
      setPublishError(err?.message || 'Failed to publish.');
      showToast('Failed to publish to gallery', 'error');
    }
    setIsPublishing(false);
  }, [item, publishSuccess, showToast]);

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-8" style={{ background: 'var(--color-background)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
          <Icon name="AlertCircle" size={32} color="var(--color-error)" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Content could not be loaded.</h2>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>The music video failed to load. Please try again.</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <button onClick={() => setLoadError(false)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-primary)', color: '#fff' }}>
            <Icon name="RefreshCw" size={15} color="#fff" /> Retry
          </button>
          <button onClick={() => navigate('/creator-dashboard')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="LayoutDashboard" size={15} /> Back to Dashboard
          </button>
          <button onClick={() => navigate('/library', { state: { filter: 'music-to-video' } })} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="Library" size={15} /> Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--color-background)' }}>
      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[300] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in-up"
          style={{
            background:
              toast?.type === 'error' ?'rgba(239,68,68,0.95)' :'rgba(16,185,129,0.95)',
            color: 'white',
            backdropFilter: 'blur(8px)',
            boxShadow: 'var(--shadow-xl)',
          }}
        >
          <Icon
            name={toast?.type === 'error' ? 'AlertCircle' : 'CheckCircle2'}
            size={16}
            color="white"
          />
          <span className="text-sm font-medium" style={{ fontFamily: 'var(--font-body)' }}>
            {toast?.message}
          </span>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-[var(--color-muted)]"
          aria-label="Go back"
        >
          <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            {item?.title || 'Music Video'}
          </h1>
          <div className="flex items-center gap-2">
            <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Music Video Preview</p>
            {isTestMode && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontFamily: 'var(--font-caption)' }}>TEST MODE</span>
            )}
            {hasRealVideo && (
              <span className="text-xs px-1.5 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981', fontFamily: 'var(--font-caption)' }}>✓ Real Video</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="LayoutDashboard" size={13} />
            Dashboard
          </button>
          <button
            onClick={() => navigate('/library', { state: { filter: 'music-to-video' } })}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Library" size={13} />
            Library
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center justify-start p-4 md:p-8 gap-6 max-w-4xl mx-auto w-full">

        {/* Video Player */}
        <div
          ref={containerRef}
          className="w-full rounded-2xl overflow-hidden relative"
          style={{ background: '#050014', aspectRatio: '16/9', border: '1px solid var(--color-border)' }}
        >
          <AudioReactiveBackground audioRef={audioRef} playing={playing} />

          {/* Real HTML5 video */}
          {hasRealVideo && (
            <video
              ref={videoRef}
              src={videoUrl}
              className="absolute inset-0 w-full h-full object-contain z-10"
              style={{ opacity: 0.95 }}
              onTimeUpdate={(e) => setCurrentTime(e?.target?.currentTime)}
              onLoadedMetadata={(e) => setDuration(e?.target?.duration)}
              onEnded={() => setPlaying(false)}
              onError={() => setLoadError(true)}
              playsInline
              muted={muted}
            />
          )}

          {/* Hidden audio for audio-only mode */}
          {hasAudio && !hasRealVideo && (
            <audio
              ref={audioRef}
              src={audioUrl}
              onTimeUpdate={(e) => setCurrentTime(e?.target?.currentTime)}
              onLoadedMetadata={(e) => setDuration(e?.target?.duration)}
              onEnded={() => setPlaying(false)}
              style={{ display: 'none' }}
              muted={muted}
            />
          )}

          {/* Thumbnail overlay when not playing */}
          {!hasRealVideo && item?.thumbnail && !playing && (
            <div className="absolute inset-0 z-10">
              <Image
                src={item?.thumbnail}
                alt={item?.thumbnailAlt || item?.title || 'Music video preview'}
                className="w-full h-full object-cover"
                style={{ opacity: 0.5 }}
              />
            </div>
          )}

          {/* Play/Pause overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-20">
            <button
              onClick={handlePlayPause}
              className="w-16 h-16 rounded-full flex items-center justify-center shadow-xl transition-transform hover:scale-110"
              style={{ background: 'linear-gradient(135deg, #F43F5E, #8B5CF6)', boxShadow: '0 0 40px rgba(244,63,94,0.5)' }}
              aria-label={playing ? 'Pause' : 'Play music video'}
            >
              <Icon name={playing ? 'Pause' : 'Play'} size={26} color="#fff" />
            </button>
          </div>

          {/* Duration badge */}
          {duration > 0 && (
            <div className="absolute bottom-3 right-3 z-30 px-2 py-1 rounded-lg text-xs font-mono" style={{ background: 'rgba(0,0,0,0.7)', color: 'white' }}>
              {formatDuration(currentTime)} / {formatDuration(duration)}
            </div>
          )}

          {/* BPM badge */}
          {item?.bpm && (
            <div className="absolute top-3 right-3 z-30 flex items-center gap-1 px-2 py-1 rounded-lg text-xs" style={{ background: 'rgba(244,63,94,0.8)', color: 'white' }}>
              <Icon name="Activity" size={11} color="white" />
              {item?.bpm} BPM
            </div>
          )}

          {/* Fullscreen button */}
          <button
            onClick={handleFullscreen}
            className="absolute top-3 left-3 z-30 w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:opacity-80"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            aria-label="Toggle fullscreen"
          >
            <Icon name={isFullscreen ? 'Minimize2' : 'Maximize2'} size={14} color="white" />
          </button>
        </div>

        {/* Waveform + Controls */}
        <div className="w-full rounded-xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Music" size={16} color="#F43F5E" />
            <span className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Music Waveform</span>
            {item?.bpm && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E' }}>~{item?.bpm} BPM</span>}
            {item?.visualStyle && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>{item?.visualStyle}</span>}
            {item?.captionMode && item?.captionMode !== 'none' && (
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>CC: {item?.captionMode}</span>
            )}
          </div>

          <WaveformBar
            waveform={item?.waveform || waveform}
            currentTime={currentTime}
            duration={duration || (item?.duration || 240)}
            onSeek={handleSeek}
          />

          {/* Seek bar */}
          <div
            className="mt-2 h-1.5 rounded-full overflow-hidden cursor-pointer"
            style={{ background: 'var(--color-muted)' }}
            onClick={handleSeekBar}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`, background: 'linear-gradient(90deg, #F43F5E, #8B5CF6)' }}
            />
          </div>

          {/* Time + Volume */}
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs font-mono" style={{ color: 'var(--color-muted-foreground)' }}>{formatDuration(currentTime)}</span>
            <div className="flex items-center gap-2">
              <button onClick={handleMute} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
                <Icon name={muted ? 'VolumeX' : volume < 0.5 ? 'Volume1' : 'Volume2'} size={14} color="var(--color-muted-foreground)" />
              </button>
              <input
                type="range" min={0} max={1} step={0.05} value={muted ? 0 : volume}
                onChange={(e) => handleVolumeChange(parseFloat(e?.target?.value))}
                className="w-20 h-1 accent-pink-500"
              />
            </div>
            <span className="text-xs font-mono" style={{ color: 'var(--color-muted-foreground)' }}>{formatDuration(duration || item?.duration || 0)}</span>
          </div>
        </div>

        {/* Info */}
        {item && (
          <div className="w-full rounded-xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title}</h2>
            {item?.prompt && <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{item?.prompt}</p>}
            <div className="flex flex-wrap gap-2 mt-2">
              {item?.createdAt && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>Created: {item?.createdAt}</span>}
              {item?.musicSyncMode && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(244,63,94,0.1)', color: '#F43F5E' }}>Sync: {item?.musicSyncMode}</span>}
              {item?.visualStyle && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>Style: {item?.visualStyle}</span>}
              {hasRealVideo && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)', color: '#10B981' }}>✓ Real Video</span>}
              {item?.duration > 0 && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(6,182,212,0.1)', color: '#06B6D4' }}>⏱ {formatDurationLabel(item?.duration)}</span>}
              {item?.durationMode && <span className="text-xs px-2 py-1 rounded-lg" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>{item?.durationMode}</span>}
            </div>
          </div>
        )}

        {/* Post-gen Editor Panel */}
        {showEditor && (
          <div className="w-full rounded-xl p-4 animate-fade-in-up" style={{ background: 'var(--color-card)', border: '1px solid rgba(139,92,246,0.3)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Scissors" size={16} color="#8B5CF6" />
                <span className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Video Editor</span>
              </div>
              <button onClick={() => setShowEditor(false)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { icon: 'Replace', label: 'Replace Scene', color: '#F43F5E' },
                { icon: 'Trash2', label: 'Remove Scene', color: '#EF4444' },
                { icon: 'ArrowUpDown', label: 'Reorder Scenes', color: '#8B5CF6' },
                { icon: 'Timer', label: 'Extend Duration', color: '#06B6D4' },
                { icon: 'Palette', label: 'Switch Style', color: '#10B981' },
                { icon: 'Subtitles', label: 'Edit Captions', color: '#F59E0B' },
                { icon: 'Shuffle', label: 'Change Transitions', color: '#6366F1' },
                { icon: 'RefreshCw', label: 'Regenerate Section', color: '#EC4899' },
              ]?.map((action) => (
                <button
                  key={action?.label}
                  className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all hover:opacity-80"
                  style={{ borderColor: `${action?.color}30`, background: `${action?.color}08` }}
                  onClick={() => {}}
                >
                  <Icon name={action?.icon} size={14} color={action?.color} />
                  <span className="text-xs font-semibold" style={{ color: action?.color, fontFamily: 'var(--font-caption)' }}>{action?.label}</span>
                </button>
              ))}
            </div>
            <p className="text-xs mt-3 text-center" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Scene editing available after full video generation</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-4 gap-3">
          <button
            onClick={handleDownload}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Download" size={20} color="var(--color-primary)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Download</span>
          </button>
          <button
            onClick={() => {
              if (navigator?.share) navigator.share({ title: item?.title, url: window.location?.href });
              else navigator?.clipboard?.writeText(window.location?.href);
            }}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Share2" size={20} color="var(--color-secondary)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Share</span>
          </button>
          <button
            onClick={() => setShowEditor((v) => !v)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: showEditor ? 'rgba(139,92,246,0.1)' : 'var(--color-card)', border: `1px solid ${showEditor ? 'rgba(139,92,246,0.4)' : 'var(--color-border)'}` }}
          >
            <Icon name="Scissors" size={20} color="#8B5CF6" />
            <span className="text-xs font-semibold" style={{ color: showEditor ? '#8B5CF6' : 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Edit</span>
          </button>
          <button
            onClick={() => navigate('/creator-dashboard', { state: { remixItem: item } })}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Shuffle" size={20} color="#F43F5E" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Remix</span>
          </button>
        </div>

        {/* Publish to Community Gallery */}
        <div className="w-full">
          {publishSuccess ? (
            <div
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold"
              style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: 'var(--color-success)' }}
            >
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
              Published to Community Gallery!
              <button
                onClick={() => navigate('/community-gallery')}
                className="ml-2 text-xs underline"
                style={{ color: 'var(--color-success)' }}
              >
                View Gallery
              </button>
            </div>
          ) : (
            <button
              onClick={handlePublishToGallery}
              disabled={isPublishing}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80 disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))',
                border: '1px solid rgba(99,102,241,0.3)',
                color: 'var(--color-primary)',
              }}
            >
              {isPublishing ? (
                <Icon name="Loader" size={16} color="var(--color-primary)" />
              ) : (
                <Icon name="Globe" size={16} color="var(--color-primary)" />
              )}
              {isPublishing ? 'Publishing...' : 'Publish to Community Gallery'}
            </button>
          )}
          {publishError && (
            <p className="mt-1 text-xs text-center" style={{ color: '#EF4444', fontFamily: 'var(--font-caption)' }}>
              {publishError}
            </p>
          )}
        </div>

        {/* Re-render button */}
        <button
          onClick={() => navigate('/creator-dashboard', { state: { mode: 'music-to-video' } })}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'linear-gradient(135deg, rgba(244,63,94,0.1), rgba(139,92,246,0.1))', border: '1px solid rgba(244,63,94,0.3)', color: '#F43F5E' }}
        >
          <Icon name="RefreshCw" size={16} color="#F43F5E" />
          Create New Music Video
        </button>

        {/* Library button */}
        <button
          onClick={() => navigate('/library', { state: { filter: 'music-to-video' } })}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
          style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', color: 'var(--color-muted-foreground)' }}
        >
          <Icon name="Library" size={16} color="var(--color-muted-foreground)" />
          View All Music Videos in Library
        </button>
      </div>
    </div>
  );
}
